import { $deleteFromArray, $handleError, $updateOrAdd } from '@/utils';
import facilityService from '@Facility/service/facility.service';
import Vue from 'vue';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import {
  FACILITY_DROPDOWN_LIMIT,
  FACILITY_DROPDOWN_CACHE_MS,
} from '@/features/Share/config/constants';

const defaultOptions = { filterType: 1, sortDesc: true };

export const FACILITY = {
  namespaced: true,
  state: {
    facility: null,
    entries: [],
    total: 1,
    loading: false,
    favLoading: null,
    options: {...defaultOptions},
    accessibleEntries: [],
    accessibleLoading: false,
    accessibleError: null,
    accessibleLoadedAt: null,
  },
  getters: {
    GET_FACILITY(state) {
      return state.facility;
    },
    GET_ENTRIES(state) {
      return state.entries;
    },
    GET_TOTAL(state) {
      return state.total;
    },
    GET_LOADING(state) {
      return state.loading;
    },
    GET_OPTIONS(state) {
      return state.options;
    },
    GET_FAV_LOADING(state) {
      return state.favLoading;
    },
    GET_ACCESSIBLE_ENTRIES(state) {
      return state.accessibleEntries;
    },
    GET_ACCESSIBLE_LOADING(state) {
      return state.accessibleLoading;
    },
    GET_ACCESSIBLE_ERROR(state) {
      return state.accessibleError;
    },
  },
  mutations: {
    SET_FACILITY(state, val) {
      state.facility = val;
    },
    SET_ENTRIES(state, value) {
      state.entries = value;
    },
    SET_TOTAL(state, value) {
      state.total = value;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_OPTIONS(state, value) {
      state.options = value;
    },
    RESET_OPTIONS(state) {
      state.options = { ...defaultOptions };
    },
    SET_FAV_LOADING(state, value) {
      state.favLoading = value;
    },
    SET_ACCESSIBLE_ENTRIES(state, value) {
      state.accessibleEntries = value;
    },
    SET_ACCESSIBLE_LOADING(state, value) {
      state.accessibleLoading = value;
    },
    SET_ACCESSIBLE_ERROR(state, value) {
      state.accessibleError = value;
    },
    SET_ACCESSIBLE_LOADED_AT(state, value) {
      state.accessibleLoadedAt = value;
    },
    UPDATE_ACCESSIBLE_FAVORITE(state, { id, isFavorite }) {
      const index = state.accessibleEntries.findIndex((f) => f.id == id);
      if (index > -1) {
        Vue.set(state.accessibleEntries, index, {
          ...state.accessibleEntries[index],
          isFavorite,
        });
      }
    },
  },
  actions: {
    GET({ commit, state }, userId) {
      commit('SET_LOADING', true);
      commit('SET_ENTRIES', []);
      state.options.UserId = userId ? userId : ''
      facilityService
        .get(state.options) //state.options
        .then((res) => {
          const data = res.data.items;
          commit('SET_ENTRIES', data);
          commit('SET_TOTAL', res.data.total);
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    },
    GET_BY_CLIENT({ commit, state }, { userId, options }) {
      commit('SET_LOADING', true);
      commit('SET_ENTRIES', []);
      const params = {
        ...state.options,
        ...options,
        UserId: userId || ''
      };
      facilityService
        .getByClient(params)
        .then((res) => {
          const data = res.data.items || res.data;
          commit('SET_ENTRIES', data);
          commit('SET_TOTAL', res.data.total || data.length);
        })
        .catch((err) => {
          $handleError(err);
          // Fallback to regular GET if client endpoint fails
          return facilityService.get(params).then((res) => {
            const data = res.data.items;
            commit('SET_ENTRIES', data);
            commit('SET_TOTAL', res.data.total);
          });
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    },
    GET_BY_ID({ commit }, { id, selectFacility }) {
      return new Promise((resolve) => {
        facilityService.getById(id).then((res) => {
          if (selectFacility) commit('SET_FACILITY', res.data);
          resolve(res);
        });
      });
    },
    UPDATE({ state, commit }, data) {
      return facilityService.update(data).then((res) => {
        $updateOrAdd(state.entries, res.data, 'id', true);
        if (!data.id) {
          commit('SET_TOTAL', ++state.total);
        }
      });
    },
    TOGGLE_FAV({ state, commit }, { id, status }) {
      commit('SET_FAV_LOADING', id);
      const toggleFav = status ? facilityService.addToFav(id) : facilityService.delFav(id);
      return toggleFav
        .then((res) => {
          // Find the existing facility to preserve all its data
          const index = state.entries.findIndex((facility) => facility.id == id);
          if (index > -1) {
            // Create a new object preserving all existing data, only updating isFavorite
            const updatedFacility = {
              ...state.entries[index],
              isFavorite: status
            };
            Vue.set(state.entries, index, updatedFacility);
          } else {
          // If facility not found, use the response data as fallback
            let data = res.data;
            data.isFavorite = status;
            $updateOrAdd(state.entries, data);
          }
          // Keep the top-bar breadcrumb dropdown in sync: mirror favorite into accessibleEntries.
          commit('UPDATE_ACCESSIBLE_FAVORITE', { id, isFavorite: status });
        })
        .finally(() => {
          commit('SET_FAV_LOADING', null);
        });
    },
    ADD_TO_RECENT(_, id) {
      return facilityService.addToRecent(id);
    },
    DELETE({ state, commit }, id) {
      return facilityService
        .del(id)
        .then(() => {
          $deleteFromArray(state.entries, id);
          commit('SET_TOTAL', --state.total);
          Vue.prototype.$dialog.notify.success('Deleted Successfully', {
            position: 'top-right',
            timeout: 3000,
          });
        })
        .catch((err) => {
          $handleError(err);
        });
    },
    /**
     * Fetches the full list of facilities the current user has access to,
     * for the top-bar breadcrumb dropdown. Cached for FACILITY_DROPDOWN_CACHE_MS.
     * Pass { force: true } to bypass the cache (e.g., after a company switch).
     */
    FETCH_ACCESSIBLE({ commit, state, rootGetters }, { force = false } = {}) {
      const now = Date.now();
      const isFresh = state.accessibleLoadedAt
        && (now - state.accessibleLoadedAt) < FACILITY_DROPDOWN_CACHE_MS;

      if (!force && isFresh) {
        debugLog('[FACILITY/FETCH_ACCESSIBLE] cache hit', {
          count: state.accessibleEntries.length,
        });
        return Promise.resolve(state.accessibleEntries);
      }

      // If a request is already in flight, don't start another.
      if (state.accessibleLoading) {
        return Promise.resolve(state.accessibleEntries);
      }

      commit('SET_ACCESSIBLE_LOADING', true);
      commit('SET_ACCESSIBLE_ERROR', null);

      const user = rootGetters['ACCOUNT/GET_USER'];
      const userId = (user && user.id) || '';

      return facilityService
        .get({
          UserId: userId,
          itemsPerPage: FACILITY_DROPDOWN_LIMIT,
        })
        .then((res) => {
          const items = (res && res.data && res.data.items) || [];
          commit('SET_ACCESSIBLE_ENTRIES', items);
          commit('SET_ACCESSIBLE_LOADED_AT', now);
          debugLog('[FACILITY/FETCH_ACCESSIBLE] fetched', { count: items.length });
          return items;
        })
        .catch((err) => {
          const message = (err && err.message) || 'Failed to load facilities';
          commit('SET_ACCESSIBLE_ERROR', message);
          debugError('[FACILITY/FETCH_ACCESSIBLE] error', err);
          throw err;
        })
        .finally(() => {
          commit('SET_ACCESSIBLE_LOADING', false);
        });
    },
    /**
     * Resolves whether the current user may access `facilityId`.
     *
     * Two-tier by design:
     *   1. Fast path — membership in `accessibleEntries` (FETCH_ACCESSIBLE,
     *      5-min cached). A hit is an immediate allow at ~0ms.
     *   2. Authoritative fallback — `facilityService.hasAccess(facilityId)`
     *      (GET api/Facilities/has-access/{id}), the dedicated server-side
     *      access check. A list MISS is deliberately NOT treated as a denial:
     *      the list is capped at FACILITY_DROPDOWN_LIMIT and its in-flight
     *      branch can resolve with a stale/empty array, so a miss is
     *      ambiguous. Only the backend answering "no" is a real denial.
     *
     * DO NOT revert this to `facilityService.getById(facilityId)`: getById
     * does NOT enforce access server-side — it returns the facility to any
     * authenticated user, so using it as the fallback allowed everyone
     * through. The dedicated has-access endpoint is the only authoritative
     * source of truth here.
     *
     * has-access semantics: 200 + facility entity when the user has access,
     * 404 when they don't. So a resolved request IS the allow signal — the
     * body is not inspected.
     *
     * Fail-open on ambiguity (list fetch error), fail-closed on the
     * authoritative check (a rejection — 403/404/network — denies).
     *
     * @param {string|number} facilityId
     * @returns {Promise<boolean>}
     */
    async CHECK_ACCESS({ dispatch }, facilityId) {
      if (!facilityId) return false;

      try {
        const entries = await dispatch('FETCH_ACCESSIBLE');
        if (Array.isArray(entries)
          && entries.some((f) => f && String(f.id) === String(facilityId))) {
          return true;
        }
      } catch (err) {
        // A list-fetch failure must NOT deny access — fall through to the
        // authoritative check below.
        debugError('[FACILITY/CHECK_ACCESS] accessible list fetch failed', err);
      }

      try {
        // 200 (entity returned) = access granted; 404 = no access → reject.
        await facilityService.hasAccess(facilityId);
        return true;
      } catch (err) {
        debugError('[FACILITY/CHECK_ACCESS] denied', { facilityId, err });
        return false;
      }
    },
  },
};
